<template>
  <ProductDetailTopLayout>
    <template #media>
      <ProductDetailImages
        :product-enrichments="productEnrichments"
        :bvs-manifest="bvsManifest"
        :bvs-loading="bvsManifestLoading"
        @select-bvs-scene="selectBvsScene"
      />
    </template>
    <template #data>
      <ProductDetailHeadSkeleton
        v-if="store.loading"
        :show-compare-checkbox="productComparisonProps !== undefined"
      />
      <template v-else>
        <div
          v-if="
            store.decoratedProduct?.isDiscontinued || pendingDiscontinuation
          "
          :class="$style.productHint"
        >
          <AlertBox icon="attention" :style="'warning'">
            <template #default>
              <DictionaryRichText
                v-if="store.decoratedProduct?.isDiscontinued"
                dictionary-key="pdp.hint.discontinued"
                :fallback-document="PDP_HINT_DISCONTINUED_FALLBACK_DOCUMENT"
              />
              <DictionaryRichText
                v-else
                dictionary-key="pdp.hint.pendingDiscontinuation"
                :fallback-document="
                  PDP_HINT_PENDING_DISCONTINUATION_FALLBACK_DOCUMENT
                "
              />
            </template>
          </AlertBox>
        </div>
        <div v-else-if="visiblePdpBanners.length > 0" data-testid="pdp-banner">
          <div
            v-for="(pdpBanner, pdpBannerIndex) in visiblePdpBanners"
            :key="`${pdpBanner.dictKey}-${pdpBannerIndex}`"
            :class="$style.productHint"
          >
            <AlertBox
              :icon="pdpBanner.icon ?? 'attention'"
              :style="pdpBanner.style ?? 'warning'"
              :style-variant="pdpBanner.styleVariant ?? 'primary'"
            >
              <DictionaryRichText :dictionary-key="pdpBanner.dictKey" />
            </AlertBox>
          </div>
        </div>
        <ProductDetailHeader
          :id="store.decoratedProduct?.name ?? ''"
          :name="store.decoratedProduct?.family ?? ''"
          :variant-name="store.decoratedProduct?.modelName ?? ''"
          :description="headerDescription"
          :attributes="
            !store.decoratedProduct?.isDiscontinued
              ? store.highlightedSpecs
              : []
          "
          :special-badge="badgeInfo.badgeText"
          :badge-style="badgeInfo.badgeStyle ?? 'primary'"
          :has-shine-animation="badgeInfo.hasShineAnimation"
          :is-loading="store.loading"
          :link="headerLink"
          :link-text="headerLinkText"
        >
          <template v-if="productComparisonProps" #compare>
            <ProductComparisonCheckbox v-bind="productComparisonProps" />
          </template>
        </ProductDetailHeader>

        <ProductDetailLogoRow
          v-if="
            !store.loading &&
            !store.decoratedProduct?.isDiscontinued &&
            store.specificationLogos.length > 0
          "
          :specification-logos="store.specificationLogos"
        />

        <div
          v-if="store.decoratedProduct?.sensorDescriptionText"
          :class="[
            $style['teaser-text-renderer'],
            'product-detail product-detail__description'
          ]"
          v-html="store.decoratedProduct?.sensorDescriptionText"
        />

        <ProductDetailFollowingProductLinks
          v-if="store.decoratedProduct?.isDiscontinued"
        />

        <ProductDetailConfigureOptions />
        <ProductDetailProductAddons />
        <div
          v-if="canLoadCrosssellingAddons"
          ref="crosssellingSentinelRef"
          class="pdp-crossselling-addons-sentinel"
          aria-hidden="true"
        />
        <ProductDetailCrosssellingProductAddons
          v-if="showCrosssellingAddonsSection"
        />
        <!--        <ProductDetailAddToCart />-->
      </template>
    </template>
  </ProductDetailTopLayout>

  <LibErrorBoundary
    v-if="showBvsSimulation && bvsManifest"
    component-name="ProductDetailBvsSimulation"
  >
    <ProductDetailBvsSimulation
      :sku="bvsSku"
      :manifest="bvsManifest"
      :selected-scene-id="selectedBvsSceneId"
      :scene-selection-key="bvsSceneSelectionKey"
    />
  </LibErrorBoundary>

  <slot name="contentSlotAboveTabs"></slot>
  <TabsSection
    v-if="!store.loading && store.decoratedProduct"
    id="product-detail-page-tabs"
    ref="tabsSectionRef"
    :key="tabs.map(({ title }) => title).join('---')"
    :tab-list-full-width="false"
    :state="tabsState"
    @tab-change="setActivePdpTab"
  >
    <template #panels>
      <!--
      TabsSection uses TabsComponent which uses a TransitionGroup.
      A TransitionGroup requires a v-for for correct rendering.

      @see https://vuejs.org/guide/built-ins/transition-group.html#differences-from-transition
      @see https://github.com/vuejs/core/issues/6745
      @see https://gcp.baslerweb.com/jira/browse/WEB2-1852
      @see https://gcp.baslerweb.com/jira/browse/WEB2-1984
      -->
      <TabPanel
        v-for="tab in tabs"
        :key="tab.id"
        :uid="tab.id"
        :active-on-init="tab.active"
        :title="tab.title"
      >
        <slot
          v-if="tab.component === LibProductEnrichmentTabElement"
          name="contentSlotTabElement"
          :product-enrichments="tab.enrichments ?? []"
        ></slot>
        <component :is="tab.component" v-else v-bind="tab.props" />
      </TabPanel>
    </template>
    <template v-if="showRelatedProductsNavLink" #tab-list-additional-content>
      <a
        id="tab-list-suitable-products"
        class="button button--no-icon button--link button--medium"
        href="#suitable-products"
        @click="onRelatedProductsNavClick"
      >
        {{
          $textDictionary['pdp.relatedproducts.link'] ?? 'See Suitable Products'
        }}
      </a>
    </template>
  </TabsSection>

  <slot name="contentSlotCenter"></slot>

  <div
    v-if="canLoadRelatedProducts"
    ref="relatedSectionRef"
    class="pdp-related-products-sentinel"
    aria-hidden="true"
  />

  <NuxtErrorBoundary @error="handleRelatedProductsError">
    <LazyVisibleShopRelatedProducts
      v-if="showRelatedProductsSection"
      hydrate-on-visible
      :generate-detail-link="createProductDetailLink"
    />
    <!--
      XXX: do we show an error or simply omit the section?
    <template #error="error">
      There was an error loading related products.
    </template>
    -->
  </NuxtErrorBoundary>
</template>

<script setup lang="ts">
import type {
  ProductCardProps,
  MediaImageInterface,
  HyperlinkInterface,
  TabsStateInterface
} from '@hypercodestudio/basler-components';
import {
  AlertBox,
  ProductDetailHeader,
  ProductDetailTopLayout,
  TabPanel,
  TabsSection
} from '@hypercodestudio/basler-components';

import LibProductEnrichmentTabElement from '~/components/global/lib/ProductEnrichmentTabElement.vue';
import ProductDetailTabSpecifications from './ProductDetailTabSpecifications.vue';
import { LazyShopRelatedProducts } from '#components';
import ProductDetailHeadSkeleton from './skeletons/ProductDetailHeadSkeleton.vue';
import ProductDetailLogoRow from './ProductDetailLogoRow.vue';
import ProductDetailConfigureOptions from './ProductDetailConfigureOptions.vue';
import ProductComparisonCheckbox from '~/components/shop/ProductComparisonCheckbox.vue';
// import ProductDetailAddToCart from './ProductDetailAddToCart.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { useRichTextDictionary } from '~/composables/useRichTextDictionary';
import {
  PDP_HINT_DISCONTINUED_FALLBACK_DOCUMENT,
  PDP_HINT_PENDING_DISCONTINUATION_FALLBACK_DOCUMENT
} from '~/utils/dictionaryRichTextFallbacks';
import { isDefined } from '~/utils/guards/isDefined';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import type { IProductEnrichment } from '~~/lib/ContentfulService';
import { isConfigurableProduct } from '~/stores/productDetailPageStore/helper/isConfigurableProduct';
import ProductDetailImages from '~/components/shop/ProductDetailImages.vue';

import { getProductImage } from '~/utils/shop/getProductImage';
import { buildPdpHeaderDescription } from '~/utils/shop/buildPdpHeaderDescription';
import {
  filterVisiblePdpBanners,
  getProductEnrichmentPdpBanners
} from '~/utils/pdpBanner/getProductEnrichmentPdpBanners';
import { ToastMessageType } from '~/composables/useShowToast';
import { ensureArray } from '~/utils/ensureArray';
import type {
  PdpDownloadsExistQueryModel,
  PdpDownloadsExistResponseModel
} from '~~/server/api/downloads/pdp/exist.get';
import { providePdpActiveTabId } from '~/composables/usePdpActiveTab';
import { usePdpTabDeepLink } from '~/composables/usePdpTabDeepLink';
import { groupProductEnrichmentTabs } from '~/utils/shop/groupProductEnrichmentTabs';

const ProductDetailTabFeature = defineAsyncComponent(
  () => import('./ProductDetailTabFeatures.vue')
);
const ProductDetailTabDocuments = defineAsyncComponent(
  () => import('./ProductDetailTabDocuments.vue')
);
const ProductDetailTabSoftware = defineAsyncComponent(
  () => import('./ProductDetailTabSoftware.vue')
);
const ProductDetailDistributors = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailDistributors.vue')
);
const ProductDetailProductAddons = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailProductAddons.vue')
);
const ProductDetailCrosssellingProductAddons = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailCrosssellingProductAddons.vue')
);

const {
  sentinelRef: crosssellingSentinelRef,
  showCrosssellingSection: showCrosssellingAddonsSection,
  canLoadCrosssellingAddons
} = usePdpCrosssellingAddonsLazyLoad();
const ProductDetailFollowingProductLinks = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailFollowingProductLinks.vue')
);
const ProductDetailBvsSimulation = defineAsyncComponent(
  () => import('~/components/shop/bvs/ProductDetailBvsSimulation.vue')
);

type TypeContentModel = {
  id: string;
  title: string;
  props?: Record<string, unknown>;
  /** Enrichments rendered by this tab; only set for enrichment tabs. */
  enrichments?: IProductEnrichment[];
  component: any;
  visible?: boolean;
  active?: boolean;
};

interface Props {
  generatePdpLink: (locale: string, product: DecoratedProduct) => string;
  companyName?: string | null;
  additionalTabContent?: TypeContentModel[];
  productEnrichments?: IProductEnrichment[];
}

declare global {
  interface Window {
    baslerIsDiscontinued: boolean;
  }
}

const props = withDefaults(defineProps<Props>(), {
  additionalTabContent: () => [],
  companyName: null,
  productEnrichments: () => []
});

defineSlots<{
  contentSlotAboveTabs?: () => unknown;
  contentSlotCenter?: () => unknown;
  contentSlotTabElement?: (props: {
    productEnrichments: IProductEnrichment[];
  }) => unknown;
}>();

const locale = useRouteParamLocale();
const logger = useLogger();
const route = useRoute();
const router = useRouter();
const store = useProductDetailPageStore();
const { $textDictionary, $analytics, $activeLocale, $globalPageSettings } =
  useNuxtApp();
const toasts = useShowToast();
const createProductDetailLink = useCreateProductDetailLink();
const { resolveProductComparison } = useProductComparison();
const { activeTabId, setActivePdpTab } = providePdpActiveTabId();

// Driving the tabs from external state lets `#tab-panel-…` links select a tab
// that only registers once its asynchronous source resolved (DBP-4500).
const tabsState = reactive<TabsStateInterface>({ tabs: [] });

usePdpTabDeepLink({
  tabsState,
  onActivate: (tabId) => {
    activeTabId.value = tabId;
  }
});

const {
  sku: bvsSku,
  manifest: bvsManifest,
  isLoading: bvsManifestLoading,
  isAvailable: showBvsSimulation
} = useBvsPdpSimulation();
const selectedBvsSceneId = ref('');
const bvsSceneSelectionKey = ref(0);

async function selectBvsScene(sceneId: string): Promise<void> {
  selectedBvsSceneId.value = sceneId;
  bvsSceneSelectionKey.value += 1;
  const simulation = await waitForElement('pdp-bvs-simulation');
  simulation?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

watch(
  bvsManifest,
  (manifest) => {
    if (
      !manifest?.scenes.some((scene) => scene.id === selectedBvsSceneId.value)
    ) {
      selectedBvsSceneId.value = manifest?.scenes[0]?.id ?? '';
    }
  },
  { immediate: true }
);

const productComparisonProps = computed(() => {
  const product = store.decoratedProduct?.product;
  if (!product) {
    return undefined;
  }
  return resolveProductComparison(
    product,
    store.decoratedProduct?.isDiscontinued ?? false
  );
});

const headerDescription = computed(() =>
  buildPdpHeaderDescription(
    store.decoratedProduct?.productDescription,
    store.decoratedProduct?.productTeaserText,
    { isDiscontinued: store.decoratedProduct?.isDiscontinued }
  )
);

const badgeInfo = computed<
  Pick<ProductCardProps, 'badgeText' | 'badgeStyle' | 'hasShineAnimation'>
>(() => {
  if (store.isPreviewProduct) {
    return {
      badgeText: 'Preview',
      badgeStyle: 'warning',
      hasShineAnimation: false
    };
  }

  const badge = store.decoratedProduct?.buildBadge();
  return badge
    ? { ...badge, badgeStyle: badge.badgeStyle || undefined }
    : { badgeText: '', badgeStyle: undefined, hasShineAnimation: true };
});

const pendingDiscontinuation = computed(() => {
  return (
    store.decoratedProduct?.discontinuationStatus === 'Pending discontinuation'
  );
});

const enrichmentPdpBanners = computed(() =>
  getProductEnrichmentPdpBanners(props.productEnrichments)
);

const enrichmentPdpBannerRichText = useRichTextDictionary(
  computed(() => enrichmentPdpBanners.value.map((banner) => banner.dictKey))
);

const visiblePdpBanners = computed(() => {
  return filterVisiblePdpBanners(
    enrichmentPdpBanners.value,
    enrichmentPdpBannerRichText.value,
    Boolean(
      store.decoratedProduct?.isDiscontinued || pendingDiscontinuation.value
    )
  );
});

const hasProductFeatureAttributes = computed(
  () => (store.decoratedProduct?.renderFeatureAttributes() ?? []).length > 0
);

const enrichmentTabGroups = computed(() =>
  groupProductEnrichmentTabs(props.productEnrichments)
);

const downloadTabVisible = ref(false);
const softwareTabVisible = ref(false);
let downloadsTabVisibilityRequestId = 0;

watch(
  () => [store.loading, store.decoratedProduct?.sku] as const,
  async ([isLoading, sku]) => {
    if (isLoading || !sku) {
      softwareTabVisible.value = false;
      downloadTabVisible.value = false;
      return;
    }

    const requestId = ++downloadsTabVisibilityRequestId;

    try {
      const response = await $fetch<PdpDownloadsExistResponseModel>(
        '/api/downloads/pdp/exist',
        {
          method: 'get',
          query: {
            locale: $activeLocale.value.contentfulCode,
            sku
          } satisfies PdpDownloadsExistQueryModel
        }
      );

      if (requestId !== downloadsTabVisibilityRequestId) {
        return;
      }

      downloadTabVisible.value = response.documentsExist;
      softwareTabVisible.value = response.softwareExist;
    } catch (error) {
      if (requestId !== downloadsTabVisibilityRequestId) {
        return;
      }

      downloadTabVisible.value = false;
      softwareTabVisible.value = false;
      logger.error('failed to fetch pdp download tab visibility', error);
    }
  },
  { immediate: true }
);

const tabs = computed<TypeContentModel[]>(() =>
  [
    ...enrichmentTabGroups.value.map((group) => ({
      pos: group.position,
      id: group.id,
      title:
        group.title ??
        $textDictionary.value['pdp.general.tabPanel.title.label'] ??
        'General',
      component: LibProductEnrichmentTabElement,
      enrichments: group.enrichments,
      visible: true
    })),
    {
      pos: 1,
      id: 'tab-specifications',
      title:
        $textDictionary.value['pdp.specifications.tabPanel.title.label'] ??
        'Specification Title',
      component: ProductDetailTabSpecifications,
      visible: true
    },
    {
      pos: 2,
      id: 'tab-features',
      title:
        $textDictionary.value['pdp.feature.tabPanel.title.label'] ??
        'Feature Tab',
      component: ProductDetailTabFeature,
      visible:
        !store.decoratedProduct?.isDiscontinued &&
        hasProductFeatureAttributes.value
    },
    {
      pos: 3,
      id: 'tab-documents',
      title:
        $textDictionary.value['pdp.documents.tabPanel.title.label'] ??
        'Documents',
      component: ProductDetailTabDocuments,
      props: { tabId: 'tab-documents' },
      visible: downloadTabVisible.value
    },
    {
      pos: 4,
      id: 'tab-software',
      title:
        $textDictionary.value['pdp.software.tabPanel.title.label'] ??
        'Software',
      component: ProductDetailTabSoftware,
      props: { tabId: 'tab-software' },
      visible: softwareTabVisible.value
    },
    {
      pos: 5,
      id: 'tab-sources-of-supply',
      title:
        $textDictionary.value['pdp.sources-of-supply.tabPanel.title.label'] ??
        'Sources of supply',
      component: ProductDetailDistributors,
      visible:
        store.decoratedProduct?.sku !== '' &&
        store.decoratedProduct?.channelPartnersData &&
        store.decoratedProduct.channelPartnersData.length > 0
    }
  ]
    .filter(({ visible }) => visible)
    .sort((a, b) => a.pos - b.pos)
    .map((tab, index) => ({ ...tab, active: index === 0 }))
);

const seoMetaTitle = computed(() => {
  const productMetaTitle = store.decoratedProduct?.metaTitle;
  const modelName = store.decoratedProduct?.modelName;
  const companyName = props.companyName ?? '';

  if (productMetaTitle && productMetaTitle.length > 0) {
    return `${productMetaTitle} | ${companyName}`;
  } else if (modelName && modelName.length > 0) {
    return `${modelName} | ${companyName}`;
  }

  return companyName;
});

const headerLinkText = computed(() => {
  if (store.decoratedProduct?.pdpActionBoxLinksData) {
    const headerLinkTextKey =
      ensureArray(
        store.decoratedProduct?.pdpActionBoxLinksData.ctf_dict_key.value
      )[0] ?? '';
    return (
      $textDictionary.value[headerLinkTextKey] ??
      ensureArray(store.decoratedProduct?.pdpActionBoxLinksData.label.value)[0]
    );
  }
  return undefined;
});

const headerLink = ref<HyperlinkInterface | string>('');

async function refreshHeaderLink(): Promise<void> {
  if (import.meta.server) {
    const externalUrl = ensureArray(
      store.decoratedProduct?.pdpActionBoxLinksData?.external_url?.value
    )[0];
    headerLink.value = externalUrl
      ? {
          uri: externalUrl,
          target: '_blank'
        }
      : '';
    return;
  }

  const enhancedHeaderLink = await store.enhancedPdpActionBoxLink();
  if (enhancedHeaderLink) {
    headerLink.value = enhancedHeaderLink;
    return;
  }

  const externalUrl = ensureArray(
    store.decoratedProduct?.pdpActionBoxLinksData?.external_url?.value
  )[0];

  headerLink.value = externalUrl
    ? {
        uri: externalUrl,
        target: '_blank'
      }
    : '';
}

watch(
  () => [store.decoratedProduct, $activeLocale.value.webapp],
  () => {
    void refreshHeaderLink();
  },
  { immediate: true }
);

const req = useRequestURL();
const hostname = useHostname();

const canonical = computed(() => {
  const copiedUrl = new URL(hostname);
  copiedUrl.pathname = req.pathname;
  copiedUrl.search = '';
  copiedUrl.hash = '';
  return copiedUrl.toString();
});

const seoProductImageUrl = computed(() => {
  let ogImageUrl = (
    store.decoratedProduct?.getProductImage(
      {
        aspectRatio: [4, 3],
        format: 'jpg'
      },
      $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
    )?.content as MediaImageInterface
  )?.src;
  if (isDefined(ogImageUrl) && ogImageUrl?.startsWith('//')) {
    ogImageUrl = `https:${ogImageUrl}`;
  }
  return ogImageUrl;
});

const seoRobots = computed(() => {
  // magento currently returns only the labels and no technical stable value,
  // so we map the displayed label to the correct value.
  const indexMap: Record<string, string> = {
    'no-index': 'noindex',
    index: 'index'
  };
  const followMap: Record<string, string> = {
    'no-follow': 'nofollow',
    follow: 'follow'
  };

  return (
    [
      indexMap[store.decoratedProduct?.seoIndex?.toLowerCase() ?? ''],
      followMap[store.decoratedProduct?.seoFollow?.toLowerCase() ?? '']
    ]
      .filter(isDefined)
      .join(', ') || undefined
  );
});

const tabsSectionRef = ref<InstanceType<typeof TabsSection> | null>(null);
const relatedSectionRef = ref<HTMLElement | null>(null);

const hasSuitableProducts = computed(() => {
  return (
    store.relatedProducts &&
    store.relatedProducts.length > 0 &&
    store.relatedProducts.some(
      (group) => group?.products && group.products.length > 0
    )
  );
});

const canLoadRelatedProducts = computed(
  () =>
    Boolean(store.decoratedProduct?.sku) &&
    !store.decoratedProduct?.isDiscontinued &&
    !store.loading
);

const showRelatedProductsSection = computed(
  () =>
    canLoadRelatedProducts.value &&
    (store.relatedProductsLoading || hasSuitableProducts.value)
);

const showRelatedProductsNavLink = computed(
  () =>
    canLoadRelatedProducts.value &&
    (hasSuitableProducts.value || !store.relatedProductsLoadAttempted)
);

async function requestRelatedProductsLoad(): Promise<void> {
  if (!canLoadRelatedProducts.value) {
    return;
  }

  try {
    await store.ensureRelatedProductsLoaded();
  } catch (error) {
    logger.error('Failed to load related products', error);
  }
}

async function onRelatedProductsNavClick(event: MouseEvent): Promise<void> {
  event.preventDefault();
  await requestRelatedProductsLoad();
  const el = await waitForElement('suitable-products');
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

let relatedProductsObserver: IntersectionObserver | null = null;

const setupRelatedProductsIntersectionObserver = (): void => {
  if (!relatedSectionRef.value || relatedProductsObserver) {
    return;
  }

  relatedProductsObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        void requestRelatedProductsLoad();
      }
    },
    {
      rootMargin: '200px'
    }
  );

  relatedProductsObserver.observe(relatedSectionRef.value);
};

const teardownRelatedProductsIntersectionObserver = (): void => {
  relatedProductsObserver?.disconnect();
  relatedProductsObserver = null;
};

watch(
  () => relatedSectionRef.value,
  (element) => {
    teardownRelatedProductsIntersectionObserver();
    if (element) {
      nextTick(() => {
        setupRelatedProductsIntersectionObserver();
      });
    }
  },
  { immediate: true }
);

watch(
  [() => route.hash, canLoadRelatedProducts],
  ([hash, canLoad]) => {
    if (hash === '#suitable-products' && canLoad) {
      void requestRelatedProductsLoad();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  teardownRelatedProductsIntersectionObserver();
});

useSeoMeta({
  title: () => seoMetaTitle.value,
  ogTitle: () => seoMetaTitle.value,
  description: () => store.decoratedProduct?.metaDescription ?? undefined,
  ogDescription: () => store.decoratedProduct?.metaDescription ?? undefined,
  ogUrl: canonical,
  ogSiteName: () => props.companyName ?? undefined,
  ogImage: () => seoProductImageUrl.value ?? undefined,
  robots: () => seoRobots.value
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonical
    }
  ]
});

watch(
  () => store.decoratedProduct,
  async () => {
    if (store.decoratedProduct) {
      $analytics?.pushToDataLayer({
        event: 'view_item',
        ecommerce: await createEcommerceTrackingData({
          cartItems: [
            {
              decoratedProduct: store.decoratedProduct,
              quantity: 1,
              // Use the server-resolved breadcrumbs so the view_item event does
              // not trigger a client-side slugById round-trip on product switch.
              breadcrumbs: store.breadcrumbs
            }
          ]
        })
      });
    }
  },
  { immediate: true }
);

watch(
  () => store.error,
  () => {
    if (store.error) {
      toasts.showToast($textDictionary.value[store.error] ?? store.error, {
        severity: ToastMessageType.ERROR
      });
    }
  }
);

useHead(() => {
  if (!store.decoratedProduct) {
    return {};
  }

  const offer = {
    '@type': 'Offer',
    price: store.decoratedProduct.price ?? undefined,
    priceCurrency: store.decoratedProduct.productCurrency ?? undefined,
    availability: store.decoratedProduct.showShippingTime
      ? 'InStock'
      : 'PreOrder'
  };

  const productImages = computed(() => {
    if (!store.decoratedProduct) {
      return [];
    }

    return getProductImage(
      store.decoratedProduct.product,
      {
        width: 2400,
        height: 1800,
        widths: [400, 800, 1600, 2400],
        aspectRatio: [4, 3]
      },
      $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
    );
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    sku: store.decoratedProduct.sku ?? undefined,
    name: store.decoratedProduct.modelName ?? undefined,
    description: store.decoratedProduct.productDescription ?? undefined,
    image: productImages.value.map((x) => x.media.content.src),
    url: props.generatePdpLink(locale.value, store.decoratedProduct) ?? '',
    offers: offer,
    brand:
      ensureArray(store.decoratedProduct.brandData[0]?.label?.value)[0] ??
      undefined
  };

  return {
    script: [
      {
        key: 'product-structured-data',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLdData),
        tagPriority: -1
      }
    ]
  };
});

function handleRelatedProductsError(args: unknown) {
  logger.error(args);
}

onMounted(async () => {
  // Note: loading state is managed by the store's loadProduct method
  // We don't manually set loading=false here anymore to allow proper loading animations

  window.baslerIsDiscontinued = store.decoratedProduct?.isDiscontinued ?? false;

  if (isConfigurableProduct(store.decoratedProduct?.product)) {
    // XXX: Fallback: Normally a page redirect occurs caused by magento configuration, in case it is missing, this code will display the cheapest variant instead of the ConfigurableProduct (parent product)
    const cheapestVariant = store.decoratedProduct?.product?.variants?.reduce(
      (prev, curr) => {
        const previousPrice =
          prev?.product?.price_range?.minimum_price?.final_price?.value;
        const currentPrice =
          curr?.product?.price_range?.minimum_price?.final_price?.value;

        if (isDefined(previousPrice) && isDefined(currentPrice)) {
          return previousPrice < currentPrice ? prev : curr;
        }

        if (!isDefined(previousPrice) && !isDefined(currentPrice)) {
          return null;
        }
        if (!isDefined(previousPrice) && isDefined(currentPrice)) {
          return curr;
        }
        if (isDefined(previousPrice) && !isDefined(currentPrice)) {
          return prev;
        }

        return null;
      }
    );

    if (cheapestVariant?.product?.url_key) {
      router.push({
        path: `/${$activeLocale.value.webapp}/shop/${
          cheapestVariant?.product?.url_key
        }`,
        query: route.query
      });
    }
  }
});
</script>

<style module>
.productHint {
  margin-bottom: 20px;
}

.productHint p {
  color: white;
}

.teaser-text-renderer p {
  color: inherit;
}

.teaser-text-renderer ul {
  list-style: disc;
  padding: revert;
  margin-block-end: var(--spacing-size-small);
}

.teaser-text-renderer ol {
  list-style: decimal;
  padding: revert;
  margin-block-end: var(--spacing-size-small);
}

.pdp-related-products-sentinel,
.pdp-crossselling-addons-sentinel {
  height: 1px;
  width: 100%;
}
</style>
